import { create } from 'zustand';
import * as immutable from 'object-path-immutable';
import { Interaction, MessageRole } from 'types';
import {
  AssistantStatusEvent,
  AssistantStreamEvent,
  readAssistantStream,
  submitPromptStream,
} from 'api/aiAssistant';
import {
  fetchConversation,
  fetchMessages,
  setMessageRating,
} from 'api/aiAssistantConversations';
import { showNotification } from '@mantine/notifications';
import { getErrorMessage } from 'helpers/getErrorMessage';
import { getStoredItem, storeItem } from 'helpers/storage';

type DisplayMode = 'modal' | 'widget';

/**
 * Which conversation the window is hosting. Picked at the FAB and fixed for
 * the life of the window: nothing inside an open chat switches it, so a
 * message meant for the assistant can never land in the member's own chat.
 */
export type ChatMode = 'ai' | 'member';

type TraceInspectorState = {
  open: boolean;
  interactionId: number | null;
  messageId: number | null;
  scrollToToolIndex: number | null;
};

type AiCoachAssistantStore = {
  isOpen: boolean;
  chatMode: ChatMode;
  displayMode: DisplayMode;
  widgetSidePaneOpen: boolean;
  selectedUserId: string | null;
  conversation: Record<string, Interaction | null>;
  isSubmitInProgress: boolean;
  isHistoryLoading: boolean;
  prefillText: string;
  unreadCount: number;
  toolStatuses: AssistantStatusEvent[];
  error: string | null;
  abortController: AbortController | null;
  traceInspector: TraceInspectorState;

  open: (mode: ChatMode) => void;
  close: () => void;
  toggleDisplayMode: () => void;
  toggleWidgetSidePane: () => void;
  selectUser: (userId: string | null) => void;
  fetchHistory: (userId: string) => Promise<void>;
  loadOlderMessages: (userId: string) => Promise<void>;
  sendMessage: (params: {
    prompt: string;
    userId: string;
    coachId: string;
    scrollToBottom: () => void;
  }) => Promise<void>;
  setMessageRating: (params: {
    userId: string;
    messageId: number;
    rating: 1 | -1 | null;
  }) => Promise<void>;
  openTraceInspector: (params: {
    interactionId: number;
    messageId: number;
    scrollToToolIndex?: number;
  }) => void;
  closeTraceInspector: () => void;
  setPrefillText: (text: string) => void;
  cleanupStream: () => void;
};

const MESSAGE_PAGE_SIZE = 30;

const createEmptyInteraction = (userId: string): Interaction => ({
  userId,
  createdAt: new Date().toString(),
  messages: [],
});

export const useAiCoachAssistantStore = create<AiCoachAssistantStore>()(
  (set, get) => ({
    isOpen: false,
    chatMode: 'ai',
    displayMode:
      getStoredItem<DisplayMode>('ai-assistant-display-mode') ?? 'widget',
    widgetSidePaneOpen:
      getStoredItem<boolean>('ai-assistant-widget-side-pane-open') ?? false,
    selectedUserId: null,
    conversation: {},
    isSubmitInProgress: false,
    isHistoryLoading: false,
    prefillText: '',
    // TODO: unreadCount is not functional yet. Currently AI only responds to user-initiated
    // messages, so there's nothing to mark as "unread". Revisit if/when AI sends proactive messages.
    unreadCount: 0,
    toolStatuses: [],
    error: null,
    abortController: null,
    traceInspector: {
      open: false,
      interactionId: null,
      messageId: null,
      scrollToToolIndex: null,
    },

    open: (mode: ChatMode) => set({ isOpen: true, chatMode: mode }),
    close: () => {
      if (get().isSubmitInProgress) {
        get().cleanupStream();
      }
      set({ isOpen: false });
    },
    toggleDisplayMode: () =>
      set((state) => {
        const next = state.displayMode === 'modal' ? 'widget' : 'modal';
        storeItem('ai-assistant-display-mode', next);
        return { displayMode: next };
      }),
    toggleWidgetSidePane: () =>
      set((state) => {
        const next = !state.widgetSidePaneOpen;
        storeItem('ai-assistant-widget-side-pane-open', next);
        return { widgetSidePaneOpen: next };
      }),

    selectUser: (userId: string | null) => {
      if (get().isSubmitInProgress) {
        get().cleanupStream();
      }
      set({ selectedUserId: userId, prefillText: '' });
      if (userId && get().conversation[userId] === undefined) {
        get().fetchHistory(userId);
      }
    },

    fetchHistory: async (userId: string) => {
      set({ isHistoryLoading: true });
      try {
        const conv = await fetchConversation(userId);
        if (!conv.interaction) {
          set((state) => ({
            conversation: {
              ...state.conversation,
              [userId]: createEmptyInteraction(userId),
            },
            isHistoryLoading: false,
          }));
          return;
        }
        const msgs = await fetchMessages(conv.interaction.id, {
          limit: MESSAGE_PAGE_SIZE,
        });
        set((state) => ({
          conversation: {
            ...state.conversation,
            [userId]: {
              id: conv.interaction!.id,
              userId: conv.interaction!.userId,
              coachId: conv.interaction!.coachId,
              createdAt: conv.interaction!.createdAt?.toISOString(),
              updatedAt: conv.interaction!.updatedAt?.toISOString(),
              messageTotal: msgs.total,
              messages: msgs.items.map((m) => ({
                ...m,
                createdAt: m.createdAt?.toISOString(),
              })),
            },
          },
          isHistoryLoading: false,
        }));
      } catch {
        set((state) => ({
          conversation: {
            ...state.conversation,
            [userId]: createEmptyInteraction(userId),
          },
          isHistoryLoading: false,
        }));
      }
    },

    loadOlderMessages: async (userId: string) => {
      const current = get().conversation[userId];
      if (!current?.id || !current.messages.length) return;
      const total = current.messageTotal ?? 0;
      if (current.messages.length >= total) return;
      const oldest = current.messages[0];
      if (!oldest?.id) return;

      set({ isHistoryLoading: true });
      try {
        const response = await fetchMessages(current.id, {
          before: oldest.id,
          limit: MESSAGE_PAGE_SIZE,
        });
        set((state) => {
          const existing = state.conversation[userId];
          if (!existing) return { isHistoryLoading: false };
          return {
            conversation: {
              ...state.conversation,
              [userId]: {
                ...existing,
                messages: [
                  ...response.items.map((m) => ({
                    ...m,
                    createdAt: m.createdAt?.toISOString(),
                  })),
                  ...existing.messages,
                ],
                messageTotal: response.total,
              },
            },
            isHistoryLoading: false,
          };
        });
      } catch {
        set({ isHistoryLoading: false });
      }
    },

    sendMessage: async ({ prompt, userId, coachId, scrollToBottom }) => {
      const current =
        get().conversation[userId] ?? createEmptyInteraction(userId);

      set({
        isSubmitInProgress: true,
        prefillText: '',
        error: null,
        toolStatuses: [],
      });

      // Optimistically render the user prompt + an empty assistant placeholder.
      let next = immutable.push(current, ['messages'], {
        role: MessageRole.user,
        content: prompt,
      });
      next = immutable.push(next, ['messages'], {
        role: MessageRole.assistant,
        content: '',
      });
      set((state) => ({
        conversation: { ...state.conversation, [userId]: next },
      }));

      scrollToBottom();

      const abortController = new AbortController();
      set({ abortController });

      const handleEvent = (event: AssistantStreamEvent) => {
        switch (event.type) {
          case 'assistant:status':
            set((state) => ({
              toolStatuses: [...state.toolStatuses, event.data],
            }));
            return;
          case 'assistant:chunk': {
            const convo = get().conversation[userId];
            if (!convo || convo.messages.length === 0) return;
            const lastIdx = convo.messages.length - 1;
            const updated = immutable.update(
              convo,
              ['messages', lastIdx, 'content'],
              (prev) => (prev || '') + event.data.delta
            );
            set((state) => ({
              conversation: { ...state.conversation, [userId]: updated },
            }));
            return;
          }
          case 'assistant:complete': {
            const convo = get().conversation[userId];
            if (convo && convo.messages.length > 0) {
              const lastIdx = convo.messages.length - 1;
              const patched = immutable.set(
                convo,
                ['messages', lastIdx, 'id'],
                event.data.assistantMessageId
              );
              set((state) => ({
                conversation: { ...state.conversation, [userId]: patched },
              }));
            }
            set({
              isSubmitInProgress: false,
              toolStatuses: [],
              abortController: null,
            });
            return;
          }
          case 'assistant:error':
            showNotification({ message: event.data.error, color: 'red' });
            set({
              isSubmitInProgress: false,
              error: event.data.error,
              toolStatuses: [],
              abortController: null,
            });
        }
      };

      try {
        const response = await submitPromptStream(
          { userId, coachId, prompt },
          abortController.signal
        );
        if (!response.body) throw new Error('No response body');

        for await (const event of readAssistantStream(response.body)) {
          handleEvent(event);
        }

        // Stream ended without explicit complete event (server closed cleanly)
        if (get().isSubmitInProgress) {
          set({
            isSubmitInProgress: false,
            toolStatuses: [],
            abortController: null,
          });
        }
      } catch (e: any) {
        if (e?.name === 'AbortError') return;

        const message = await getErrorMessage(e);
        showNotification({ message, color: 'red' });
        set({
          isSubmitInProgress: false,
          error: message,
          toolStatuses: [],
          abortController: null,
        });
      }
    },

    setMessageRating: async ({ userId, messageId, rating }) => {
      const current = get().conversation[userId];
      if (!current?.id) return;
      const idx = current.messages.findIndex((m) => m.id === messageId);
      if (idx === -1) return;
      const previousRating = current.messages[idx].rating ?? null;

      const optimistic = immutable.set(
        current,
        ['messages', idx, 'rating'],
        rating
      );
      set((state) => ({
        conversation: { ...state.conversation, [userId]: optimistic },
      }));

      try {
        await setMessageRating(current.id, messageId, rating);
      } catch (e) {
        const reverted = immutable.set(
          get().conversation[userId] ?? current,
          ['messages', idx, 'rating'],
          previousRating
        );
        set((state) => ({
          conversation: { ...state.conversation, [userId]: reverted },
        }));
        const message = await getErrorMessage(e);
        showNotification({ message, color: 'red' });
      }
    },

    openTraceInspector: ({ interactionId, messageId, scrollToToolIndex }) =>
      set({
        traceInspector: {
          open: true,
          interactionId,
          messageId,
          scrollToToolIndex: scrollToToolIndex ?? null,
        },
      }),

    closeTraceInspector: () =>
      set({
        traceInspector: {
          open: false,
          interactionId: null,
          messageId: null,
          scrollToToolIndex: null,
        },
      }),

    setPrefillText: (text: string) => set({ prefillText: text }),

    cleanupStream: () => {
      const controller = get().abortController;
      if (controller) {
        controller.abort();
        set({
          abortController: null,
          isSubmitInProgress: false,
          toolStatuses: [],
        });
      }
    },
  })
);
